import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, LayoutGrid, Bookmark } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTemplates, QRTemplate, UserTemplate } from '@/hooks/useTemplates';
import { TemplateCard } from './TemplateCard';
import { TemplateFilters } from './TemplateFilters';
import { UserTemplateCard } from './UserTemplateCard';
import { useAuthStore } from '@/stores/authStore';
import { useSubscription } from '@/hooks/useSubscription';

interface TemplateGalleryProps {
  onSelectTemplate: (template: QRTemplate | UserTemplate) => void;
}

export function TemplateGallery({ onSelectTemplate }: TemplateGalleryProps) {
  const { templates, userTemplates, isLoading, incrementUsage, deleteUserTemplate } = useTemplates();
  const { user } = useAuthStore();
  const { plan } = useSubscription();
  const isPremiumUser = plan !== 'free';
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showFreeOnly, setShowFreeOnly] = useState(false);
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'name'>('popular');
  const [activeTab, setActiveTab] = useState('gallery');

  // Filter and sort templates
  const filteredTemplates = useMemo(() => {
    let result = [...templates];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(query) ||
          t.description?.toLowerCase().includes(query) ||
          t.category.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (selectedCategory !== 'All') {
      result = result.filter((t) => t.category === selectedCategory);
    }

    // Free only filter
    if (showFreeOnly) {
      result = result.filter((t) => !t.is_premium);
    }

    // Sort
    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => b.usage_count - a.usage_count);
        break;
      case 'newest':
        result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
    }

    return result;
  }, [templates, searchQuery, selectedCategory, showFreeOnly, sortBy]);

  const handleUseTemplate = (template: QRTemplate) => {
    incrementUsage.mutate(template.id);
    onSelectTemplate(template);
  };

  const handleUseUserTemplate = (template: UserTemplate) => {
    onSelectTemplate(template);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="gallery" className="flex items-center gap-2">
            <LayoutGrid className="h-4 w-4" />
            Template Gallery
          </TabsTrigger>
          <TabsTrigger value="saved" className="flex items-center gap-2">
            <Bookmark className="h-4 w-4" />
            My Templates
            {userTemplates.length > 0 && (
              <span className="ml-1 text-xs bg-primary/10 px-1.5 rounded-full">
                {userTemplates.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="gallery" className="mt-6">
          <TemplateFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            showFreeOnly={showFreeOnly}
            onFreeOnlyChange={setShowFreeOnly}
            sortBy={sortBy}
            onSortChange={setSortBy}
          />

          {filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No templates found matching your filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <AnimatePresence mode="popLayout">
                {filteredTemplates.map((template, index) => (
                  <motion.div
                    key={template.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ delay: index * 0.02 }}
                  >
                    <TemplateCard
                      template={template}
                      onUse={handleUseTemplate}
                      isPremiumUser={isPremiumUser}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </TabsContent>

        <TabsContent value="saved" className="mt-6">
          {!user ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Sign in to save and manage your custom templates.</p>
            </div>
          ) : userTemplates.length === 0 ? (
            <div className="text-center py-12">
              <Bookmark className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground mb-2">No saved templates yet.</p>
              <p className="text-sm text-muted-foreground">
                Create a QR code and click "Save as Template" to reuse your designs.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {userTemplates.map((template, index) => (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <UserTemplateCard
                    template={template}
                    onUse={handleUseUserTemplate}
                    onDelete={() => deleteUserTemplate.mutate(template.id)}
                  />
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
